import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { createContext, use, useCallback, useMemo, type ReactNode } from 'react';
import { api, ApiError } from '../api/client.js';

/**
 * Session state for the web client.
 *
 * The session itself lives in an `httpOnly` cookie the client cannot read, so "am I signed in"
 * is answered by asking the server, not by inspecting storage. `/api/v1/me` is that question:
 * a 401 means signed out, and any other failure is a transport problem, not a sign-out.
 *
 * Treating a network error as a sign-out would throw someone back to the login screen every time
 * their train enters a tunnel.
 */

export interface Workspace {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly role: 'owner' | 'admin' | 'member' | 'auditor';
}

export interface AuthState {
  readonly status: 'loading' | 'signed-in' | 'signed-out' | 'unreachable';
  readonly userId: string | null;
  readonly email: string | null;
  /** False until the address is confirmed. Connecting a mailbox will require it. */
  readonly emailVerified: boolean;
  readonly workspaces: readonly Workspace[];
  /**
   * The caller's role on the deployment itself, or null. It decides whether the admin area is
   * offered — never whether a request is allowed, which the server settles on its own (INVARIANT
   * I3): a client that could grant itself an administrator would not be a control at all.
   */
  readonly platformRole: 'owner' | 'admin' | 'operator' | null;
  /** The workspace whose data the shell is showing. */
  readonly activeWorkspace: Workspace | null;
  readonly selectWorkspace: (workspaceId: string) => void;
  readonly refresh: () => Promise<void>;
  readonly signOut: () => Promise<void>;
}

const ACTIVE_WORKSPACE_KEY = 'mailbutler.workspace';

const AuthContext = createContext<AuthState | null>(null);

type MeResponse = Awaited<ReturnType<typeof fetchMe>>;

function fetchMe() {
  return api.get('/api/v1/me');
}

function readStoredWorkspace(): string | null {
  try {
    return localStorage.getItem(ACTIVE_WORKSPACE_KEY);
  } catch {
    return null;
  }
}

function storeWorkspace(id: string): void {
  try {
    localStorage.setItem(ACTIVE_WORKSPACE_KEY, id);
  } catch {
    // A viewer who cannot persist the choice still gets it for this session.
  }
}

function statusFor(query: UseQueryResult<MeResponse, Error>): AuthState['status'] {
  if (query.isPending) return 'loading';
  if (query.isSuccess) return 'signed-in';
  return query.error instanceof ApiError && query.error.status === 401
    ? 'signed-out'
    : 'unreachable';
}

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    // A 401 is a definitive answer, not a transient failure worth retrying.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status === 401) && failureCount < 2,
    staleTime: 60_000,
  });

  const workspaces: Workspace[] = useMemo(
    () =>
      (query.data?.tenants ?? []).map((tenant) => ({
        id: tenant.id,
        slug: tenant.slug,
        name: tenant.name,
        role: tenant.role,
      })),
    [query.data],
  );

  const activeWorkspace = useMemo(() => {
    const stored = readStoredWorkspace();
    return workspaces.find((workspace) => workspace.id === stored) ?? workspaces[0] ?? null;
  }, [workspaces]);

  const selectWorkspace = useCallback(
    (workspaceId: string) => {
      storeWorkspace(workspaceId);
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['me'] });
  }, [queryClient]);

  const signOut = useCallback(async () => {
    await api.post('/api/v1/auth/logout').catch(() => undefined);
    // Everything cached belongs to the session that just ended.
    queryClient.clear();
    await queryClient.invalidateQueries({ queryKey: ['me'] });
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      status: statusFor(query),
      userId: query.data?.userId ?? null,
      email: query.data?.email ?? null,
      emailVerified: query.data?.emailVerified ?? false,
      platformRole: query.data?.platformRole ?? null,
      workspaces,
      activeWorkspace,
      selectWorkspace,
      refresh,
      signOut,
    }),
    [query, workspaces, activeWorkspace, selectWorkspace, refresh, signOut],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const value = use(AuthContext);
  if (value === null) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return value;
}
